import {
  ACTION_BUSINESS_SEARCH_RESULT_CLICKED,
  CATEGORY_BUSINESS_PAGE,
} from 'utils/analytics/eventTypesConstants';
import {
  ACTION_SEARCH_BUSINESS_CALENDAR,
  ACTION_SELECT_BOOKING_TIME,
  EventCustomAttributes,
} from 'utils/analytics/eventTypeConstantsAmplitude';
import { DateTimeFormatter } from 'js-joda';
import { Dialog, DialogContent, DialogHeading, Typography } from 'omakase';
import { Head } from 'head/Head';
import { LockOptions, LockRequest } from 'types';
import { PropsToPass } from 'routes/business/SearchModalRoute';
import { ReservationSearchResults } from 'components/business/search/ReservationSearchResults';
import { Result, useBusinessCalendar } from 'hooks/calendar/useBusinessCalendar';
import { SearchSizeType } from 'utils/searchBar';
import { StoreType, useActions } from '@tock/shared/utils/connect';
import { businessToData, experienceToData } from 'utils/analytics/amplitude';
import { consumer, core, useAppContext } from '@tock/coretypes';
import {
  convertTicketTypeToParams,
  isSupportedApplePayEnvironment,
} from 'hooks/apple-pay/useIsSupportedApplePayEnvironment';
import { ga4SendEvent } from 'components/consent-restricted-scripts/GoogleAnalytics';
import { getLockedDateTimeFor, getLockedSeatingAreaFor } from 'utils/lock';
import { getNextRelease } from 'utils/businessUtils';
import {
  getPurchaseSizeFromPartySize,
  offeringConsumerDetailPageRelativeLink,
} from '@tock/shared/utils/offerings';
import { getRemainingEventTicketText } from 'utils/offerings';
import { getTitleSuffix } from 'utils/seo';
import { isQuickCheckoutEligibleExperience } from 'utils/quickCheckoutUtils';
import { isWaitlistNotificationType } from 'utils/typeGuards';
import { offeringsSelector } from 'selectors/calendarSelectors';
import { parse, stringify } from '@tock/query-string';

import { hasIn, isEmpty, isEqual, keys, map, toString } from 'es-toolkit/compat';
import { parseEncodedObjectParam } from 'utils/urlUtils';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useDidUpdateEffect, useMountEffect, usePrevious } from '@tock/hooks';
import { useFeatureFlagForConsumer } from 'featureFlags/useConsumerFeatureFlag';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import BallPulse from '@tock/shared/components/BallPulse';
import ConfirmSelection from 'components/business/search/ConfirmSelection';
import ExperienceSearchStatus from './ExperienceSearchStatus';
import FocusedPanelSeparator from 'components/focused-panel/FocusedPanelSeparator';
import OfferingsStatusMessage from 'components/business/offering/OfferingsStatusMessage';
import OpenTableExternalLink, {
  getExternalOpenTableUrl,
} from 'components/business/landing-page/OpenTableExternalLink';
import ReservationSearchBar from 'components/business/landing-page/ReservationSearchBar/ReservationSearchBar';
import ReservationSearchPartners from 'components/business/search/ReservationSearchPartners';
import SearchFooter from 'components/business/search/SearchFooter';
import SearchLoading from 'components/business/search/SearchLoading';
import WalkinWaitlistActionCard from 'components/business/walkin-waitlist/WalkinWaitlistActionCard';
import clsx from 'clsx';

type Props = PropsToPass;

interface State {
  navigatingExperienceId: number | null;
  navigationTransition: boolean;
}

interface MatchParams {
  businessDomain: string;
  widget?: string;
}

type ParsedSearchParams = { date: string; time: string; partySize: number };

/**
 * These types are being used to learn about the state of a search result when joining
 * the waitlist and will be included with our event tracking.  No application logic
 * is based on this information. This is currently only set with offering search results
 * (SearchResultMini). That is the only code path where you can join where there may be availability.
 */
export type WaitlistRequestStatus = 'AVAILABLE' | 'PARTIALLY_SOLD_OUT' | 'SOLD_OUT';
export type WaitlistRequestMetadata = {
  status: WaitlistRequestStatus;
  nearestAvailabilityMinutes?: number;
};

const ExperienceSearchRoute: React.FunctionComponent<Props> = ({
  isWidget,
  onSearch,
  onClose,
  onSelectTime,
  isSearching,
}) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const location = useLocation();
  const match = useRouteMatch<MatchParams>();
  const { context } = useAppContext();
  const { actions } = useActions();

  const {
    app,
    app: { config, jwtToken },
    lock,
    ui: { showConfirmSelectionDialog },
    offerings,
    queryParam,
  } = useSelector(mapStateToProps);
  const business = config?.business;
  const [state, setState] = useState<State>({
    navigationTransition: false,
    navigatingExperienceId: null,
  });

  const {
    availableExperiences,
    soldOutExperiences,
    availableEvents,
    soldOutEvents,
    partySizeDates: { offeredDates, soldDates, lockedDates, availableDates },
    search: {
      isValidQuery: validQuery,
      requestInProgress: isCalendarLoading,
      params: { date: selectedDate, partySize: selectedPartySize, time: selectedTime },
      offeringsPresent,
      calendarPresent,
    },
    getExclusivityAvailable,
    getRemainingTicketsForEvent,
  } = useBusinessCalendar();

  const fetchCalendarDates = useFeatureFlagForConsumer('FETCH_CALENDAR_DATES');

  const prevCalendarLoading = usePrevious(isCalendarLoading);
  const prevLocation = usePrevious(location);

  useDidUpdateEffect(() => {
    // send search event after the calendar is initialized or search params changed
    const hasInitialized = prevCalendarLoading && !isCalendarLoading;
    const hasSearched = !isEqual(prevLocation?.search, location.search);

    if (hasInitialized || hasSearched) {
      sendBusinessCalendarSearchEvent();
    }
  });

  useMountEffect(() => {
    const { partySize } = getSearchParams();
    if (fetchCalendarDates) {
      actions.calendar.fetchDates({ partySize, businessId: business?.id || undefined });
      actions.calendar.fetchDates({ businessId: business?.id || undefined });
    }
  });

  const widgetPathMod = () => {
    return isWidget ? '/widget' : '';
  };

  const getSearchParams = () => {
    const search = parse(location.search);

    let partySize = 2;
    if (search.size) {
      partySize = parseInt(search.size);
    } else if (search.party_size) {
      partySize = parseInt(search.party_size);
    }

    const waitlistNotificationType = isWaitlistNotificationType(search.notificationType)
      ? search.notificationType
      : undefined;
    const customData = parseEncodedObjectParam(search);

    const searchParams = {
      partySize,
      date: search.date,
      time: search.time,
    };

    return {
      ...searchParams,
      hasExactMatch: hasExactMatch(searchParams),
      waitlistNotificationType: waitlistNotificationType,
      customData: customData,
    };
  };

  /**
   * Returns true if the current search has availability for the
   * date, time + party size params.
   */
  const hasExactMatch = (params: ParsedSearchParams): EventCustomAttributes['hasExactMatch'] => {
    const allAvailable = [...availableEvents, ...availableExperiences];
    const { date, time, partySize } = params;

    if (!date || !time || !partySize) {
      return 'NOT_APPLICABLE';
    }

    return allAvailable.some((ticketType) => {
      return Object.keys(ticketType.availableTimes).includes(time);
    });
  };

  const sendBusinessCalendarSearchEvent = () => {
    amplitudeSendEvent(ACTION_SEARCH_BUSINESS_CALENDAR, {
      ...getSearchParams(),
      ...businessToData(business),
    });
  };

  const handleSearch = (size: number, date: string, time: string) => {
    if (!location) {
      return;
    }

    const newSearchParams: Dictionary<string> = {
      date,
      size: toString(size),
      time,
    };

    const oldSearchParams = parse(location.search);
    if (hasIn(oldSearchParams, 'disableSearch')) {
      newSearchParams.disableSearch = oldSearchParams.disableSearch;
    }

    if (!isEqual(oldSearchParams, newSearchParams)) {
      if (fetchCalendarDates) {
        actions.calendar.fetchDates({ partySize: size, businessId: business?.id || undefined });
      }
      onSearch({
        ...location,
        search: stringify(newSearchParams),
      });
    }
  };

  const handleShowWaitlist = (experienceId?: number, requestMetadata?: WaitlistRequestMetadata) => {
    const { history } = context;
    const { params } = match;
    const status = requestMetadata?.status;
    const nearestAvailabilityMinutes = requestMetadata?.nearestAvailabilityMinutes;
    const { date, size, time } = parse(location.search);
    const search = stringify({ experienceId, size, date, time });

    setState({
      navigationTransition: true,
      navigatingExperienceId: experienceId || null,
    });
    setTimeout(() => {
      setState({
        navigationTransition: false,
        navigatingExperienceId: null,
      });
      history.push({
        pathname: `/${params.businessDomain}${widgetPathMod()}/waitlist`,
        search,
        state: { status, nearestAvailabilityMinutes },
      });
    }, 450);
  };

  const handleNavigateToExperience = (experience: consumer.Offering, nextDate?: string) => {
    const { params } = match;
    const { history } = context;
    const { size, time, date: oldDate } = parse(location.search);
    const date = nextDate ? nextDate : oldDate;
    const cameFrom = 'search_modal';

    setState({
      navigationTransition: true,
      navigatingExperienceId: experience.id,
    });
    setTimeout(() => {
      setState({
        navigationTransition: false,
        navigatingExperienceId: null,
      });
      history.push({
        pathname: `/${
          params.businessDomain
        }${widgetPathMod()}${offeringConsumerDetailPageRelativeLink(experience)}`,
        search: stringify({ size, date, time, cameFrom, showExclusives: 'true' }),
      });
    }, 450);
  };

  const handleSelectTime = (
    request: LockRequest,
    options: LockOptions,
    experience: core.ITicketType
  ) => {
    const { isFeatureEnabled } = context;
    const { ticketTypeId, partySize, dateTime, priceCents } = request;
    window.analytics.sendEvent(CATEGORY_BUSINESS_PAGE, ACTION_BUSINESS_SEARCH_RESULT_CLICKED);

    const isLoginRequired = experience
      ? isQuickCheckoutEligibleExperience(experience, priceCents, isFeatureEnabled, partySize)
      : true;

    const supportedApplePayEnvironment = isSupportedApplePayEnvironment(
      convertTicketTypeToParams(experience)
    );

    const analyticsProperties = {
      ...businessToData(business),
      ...experienceToData(experience),
      reservationDateTime: dateTime.format(DateTimeFormatter.ofPattern('yyyy-MM-dd hh:mm:ss')),
      amountCents: priceCents,
      partySize,
      isLoginRequired,
      hasAddOns: !isEmpty(experience.optionGroup || []),
      isSupportedApplePayEnvironment: supportedApplePayEnvironment,
    };

    amplitudeSendEvent(ACTION_SELECT_BOOKING_TIME, analyticsProperties);
    ga4SendEvent(ACTION_SELECT_BOOKING_TIME, analyticsProperties);

    onSelectTime(
      ticketTypeId,
      partySize,
      dateTime,
      priceCents,
      options,
      business as consumer.IBusinessConfig
    );
  };

  const getRemainingTicketsForEvents = (availableEvent: Result) => {
    if (
      keys(availableEvent.availableDates).length === 1 &&
      keys(availableEvent.availableTimes).length === 1
    ) {
      const tickets = getRemainingTicketsForEvent(availableEvent.ticketType.id);
      return tickets ? getRemainingEventTicketText(tickets) : undefined;
    }
    return undefined;
  };

  if (!business) {
    return null;
  }

  const query = parse(location.search);
  const isAuthenticated = !!jwtToken;
  const nextRelease = getNextRelease(config);

  const businessName = business.name ?? undefined;

  const resultMetadata = (
    <Head title={`${business.name} - Reservations${getTitleSuffix(business)}`} />
  );

  const checkAvailabilityAriaLabel =
    'Search availability' + (businessName ? ` at ${businessName}` : '');

  if (isCalendarLoading || !validQuery) {
    return (
      <Dialog onClose={onClose} open={true} aria-label={checkAvailabilityAriaLabel}>
        <DialogHeading onClose={onClose}>{businessName}</DialogHeading>
        <DialogContent disablePadding>
          {resultMetadata}
          <SearchLoading
            location={location}
            isAuthenticated={isAuthenticated}
            isLoading={isCalendarLoading}
            isError={!isEmpty(query) && !validQuery}
          />
        </DialogContent>
      </Dialog>
    );
  }

  if (!offeringsPresent || !calendarPresent) {
    return (
      <Dialog onClose={onClose} open={true} aria-label={checkAvailabilityAriaLabel}>
        <DialogHeading onClose={onClose}>{businessName}</DialogHeading>
        <DialogContent disablePadding>
          {resultMetadata}
          <div className="SearchModal-body">
            {business.name && nextRelease && (
              <OfferingsStatusMessage businessName={business.name} nextRelease={nextRelease} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const acceptsWalkins = business.acceptsWalkins;

  const className = clsx('SearchModalExperiences', {
    'is-animating': !isSearching,
  });
  const walkinWaitlistEnabled =
    context.isFeatureEnabled('ONLINE_WALKIN_WAITLIST') && business.onlineWalkinWaitlistEnabled;

  const hasExperiencesForDate = availableExperiences.length > 0 || soldOutExperiences.length > 0;
  const hasEventsForDate = availableEvents.length > 0 || soldOutEvents.length > 0;

  return (
    <Dialog
      onClose={onClose}
      open={true}
      data-testid="in-business-search-modal"
      aria-label={checkAvailabilityAriaLabel}
    >
      <DialogHeading onClose={onClose}>{businessName}</DialogHeading>
      <DialogContent disablePadding>
        {resultMetadata}
        {query.disableSearch !== 'true' && (
          <div className="SearchBarModalContainer">
            <ReservationSearchBar
              hasExpandedCalendar
              hasInitialFocus
              isInstant
              availableDates={availableDates}
              business={business}
              date={selectedDate}
              lockedDates={lockedDates}
              offeredDates={offeredDates}
              offerings={offerings}
              size={selectedPartySize}
              soldDates={soldDates}
              time={selectedTime}
              onSearch={handleSearch}
              searchSizeType={SearchSizeType.PARTY_SIZE}
            />
          </div>
        )}
        <div className="SearchModal-body">
          {isSearching && (
            <div className="SearchModal-bodyOverlay">
              <BallPulse />
            </div>
          )}

          {!isSearching && business.externalReservationsUrl && (
            <div className="SearchModal-externalReservations">
              <div className="SearchModal-experienceSectionHeader">Reservations</div>
              <OpenTableExternalLink
                href={getExternalOpenTableUrl(
                  business.externalReservationsUrl,
                  selectedPartySize,
                  selectedDate,
                  selectedTime
                )}
              />
            </div>
          )}

          {!isSearching && business.externalReservationsUrl && (
            <div className="SearchModal-experienceSectionHeader">Experiences</div>
          )}

          <ExperienceSearchStatus
            onShowWaitlist={handleShowWaitlist}
            offerings={offerings}
            business={business}
          />

          {hasExperiencesForDate && (
            <>
              <FocusedPanelSeparator key="panelSeparator" />
              <div className={className}>
                {map(availableExperiences, (result, index) => {
                  const ticketType = result.ticketType;
                  let lockedDateTime = undefined;
                  let lockedSeatingArea = undefined;
                  if (lock.currentLock) {
                    lockedDateTime = getLockedDateTimeFor(
                      lock.currentLock,
                      ticketType.id,
                      selectedPartySize
                    );
                    lockedSeatingArea = getLockedSeatingAreaFor(
                      lock.currentLock,
                      ticketType.id,
                      selectedPartySize
                    );
                  }
                  return (
                    offerings && (
                      <ReservationSearchResults
                        availableDates={result.availableDates}
                        availableTimes={result.availableTimes}
                        business={business}
                        exclusivityAvailable={getExclusivityAvailable(ticketType.id)}
                        offerings={offerings}
                        key={result.ticketType.id}
                        lockedDateTime={lockedDateTime || undefined}
                        lockedSeatingArea={lockedSeatingArea || undefined}
                        navigatingExperienceId={state.navigatingExperienceId}
                        navigationTransition={state.navigationTransition}
                        selectedDate={selectedDate}
                        selectedPartySize={selectedPartySize}
                        selectedPurchaseSize={getPurchaseSizeFromPartySize(
                          selectedPartySize,
                          ticketType.singleUnitQuantity
                        )}
                        selectedTime={selectedTime}
                        showWaitlist={!!business.showWaitlist}
                        ticketType={result.ticketType}
                        times={result.times}
                        onSelect={(request: LockRequest, options: LockOptions) =>
                          handleSelectTime(request, options, result.ticketType)
                        }
                        onShowWaitlist={handleShowWaitlist}
                        onNavigateToExperience={handleNavigateToExperience}
                        includeAccessKey={index === 0}
                      />
                    )
                  );
                })}
                {map(soldOutExperiences, (result) => {
                  const ticketType = result.ticketType;
                  return (
                    offerings && (
                      <ReservationSearchResults
                        availableDates={result.availableDates}
                        availableTimes={result.availableTimes}
                        business={business}
                        offerings={offerings}
                        key={result.ticketType.id}
                        navigatingExperienceId={state.navigatingExperienceId}
                        navigationTransition={state.navigationTransition}
                        selectedDate={selectedDate}
                        selectedPartySize={selectedPartySize}
                        selectedPurchaseSize={getPurchaseSizeFromPartySize(
                          selectedPartySize,
                          ticketType.singleUnitQuantity
                        )}
                        selectedTime={selectedTime}
                        showWaitlist={!!business.showWaitlist}
                        ticketType={ticketType}
                        times={result.times}
                        onSelect={(request: LockRequest, options: LockOptions) =>
                          handleSelectTime(request, options, ticketType)
                        }
                        onShowWaitlist={handleShowWaitlist}
                        onNavigateToExperience={handleNavigateToExperience}
                      />
                    )
                  );
                })}
              </div>
            </>
          )}

          {hasEventsForDate && (
            <>
              <div className={className}>
                {map(availableEvents, (availableEvent) => {
                  const ticketType = availableEvent.ticketType;
                  const callToActionHint = getRemainingTicketsForEvents(availableEvent);
                  return (
                    offerings && (
                      <ReservationSearchResults
                        availableDates={availableEvent.availableDates}
                        availableTimes={availableEvent.availableTimes}
                        business={business}
                        callToActionHint={callToActionHint}
                        offerings={offerings}
                        key={ticketType.id}
                        navigatingExperienceId={state.navigatingExperienceId}
                        navigationTransition={state.navigationTransition}
                        selectedDate={selectedDate}
                        selectedPartySize={selectedPartySize}
                        selectedPurchaseSize={getPurchaseSizeFromPartySize(
                          selectedPartySize,
                          ticketType.singleUnitQuantity
                        )}
                        selectedTime={selectedTime}
                        showWaitlist={!!business.showWaitlist}
                        ticketType={ticketType}
                        times={availableEvent.times}
                        onSelect={(request: LockRequest, options: LockOptions) =>
                          handleSelectTime(request, options, ticketType)
                        }
                        onShowWaitlist={handleShowWaitlist}
                        onNavigateToExperience={handleNavigateToExperience}
                      />
                    )
                  );
                })}
                {map(soldOutEvents, (soldEvent) => {
                  const ticketType = soldEvent.ticketType;
                  return (
                    offerings && (
                      <ReservationSearchResults
                        availableDates={soldEvent.availableDates}
                        availableTimes={soldEvent.availableTimes}
                        business={business}
                        offerings={offerings}
                        key={ticketType.id}
                        navigatingExperienceId={state.navigatingExperienceId}
                        navigationTransition={state.navigationTransition}
                        selectedDate={selectedDate}
                        selectedPartySize={selectedPartySize}
                        selectedPurchaseSize={getPurchaseSizeFromPartySize(
                          selectedPartySize,
                          ticketType.singleUnitQuantity
                        )}
                        selectedTime={selectedTime}
                        showWaitlist={!!business.showWaitlist}
                        ticketType={ticketType}
                        times={soldEvent.times}
                        onSelect={(request: LockRequest, options: LockOptions) =>
                          handleSelectTime(request, options, ticketType)
                        }
                        onShowWaitlist={handleShowWaitlist}
                        onNavigateToExperience={handleNavigateToExperience}
                      />
                    )
                  );
                })}
              </div>
            </>
          )}

          {!availableDates[selectedDate] && (
            <ReservationSearchPartners
              date={selectedDate}
              partySize={selectedPartySize}
              time={selectedTime}
              message={
                <Typography color="inherit" component="span" variant="body02">
                  Still want a reservation with this party size and date? Consider one of our
                  partner restaurants nearby.
                </Typography>
              }
            />
          )}
        </div>

        {walkinWaitlistEnabled ? (
          <div className="SearchModal-waitlistCardContainer">
            <WalkinWaitlistActionCard business={business}></WalkinWaitlistActionCard>
          </div>
        ) : (
          <SearchFooter
            acceptsWalkins={acceptsWalkins || false}
            businessName={business.name || ''}
            businessEmail={business.email}
            businessPhone={business.customerFacingPhone || ''}
          />
        )}

        <ConfirmSelection
          app={app}
          business={business}
          explorePath={app.settings?.EXPLORE_PATH}
          lock={lock}
          queryParam={queryParam}
          lockActions={actions.lock}
          selectionActions={actions.selection}
          setConfirmSelectionDialogVisible={actions.ui.setConfirmSelectionDialogVisible}
          showConfirmSelectionDialog={showConfirmSelectionDialog}
        />
      </DialogContent>
    </Dialog>
  );
};

const mapStateToProps = (state: StoreType) => ({
  app: state.app,
  calendar: state.calendar,
  selection: state.selection,
  ui: state.ui,
  lock: state.lock,
  queryParam: state.queryParam,
  offerings: offeringsSelector(state),
});

export default ExperienceSearchRoute;
